import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogActions, Grid, Slide } from "@mui/material";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import { GradientColorHeader } from "@Local/Components/Dialog/";
import { Button, IconButton, Icons, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@curves/ui-base';

import { useCustomerResourceContext } from "@Local/CustomerResource/Components/CustomerResourceContext";
import useCustomerResourceApi from "@Apis/useCustomerResourceApi";
// 可拖曳的表格行組件
function SortableRow({ row, children }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
	const style = {
		transform: CSS.Transform.toString(transform), // 拖曳時的變形
		transition, // 拖曳時的過渡效果
		background: isDragging ? "rgba(0,0,0,0.04)" : undefined, // 拖曳時的背景色
	};
	return (
		<TableRow ref={setNodeRef} style={style} hover>
			{children}
			<TableCell>
				{/* 拖曳把手：只在這顆按鈕上綁 listeners/attributes */}
				<IconButton
					size="small"
					aria-label="drag"
					{...listeners}
					{...attributes}
					onMouseEnter={e => (e.target.style.cursor = "grabbing")}
					onMouseLeave={e => (e.target.style.cursor = "grab")}
				>
					<DragIndicatorIcon fontSize="inherit" />
				</IconButton>
			</TableCell>
		</TableRow>
	);
}
const CustomerResourceSortDialog = ({ open = false, onClose, refresh: refreshProp }) => {
	const { sourceData: contextSourceData, refresh: contextRefresh } = useCustomerResourceContext();
	const { postCustomerResourceSortApi, getCustomerResourceSortApi } = useCustomerResourceApi();
	const [submitting, setSubmitting] = useState(false);
	const [sortedItems, setSortedItems] = useState([]);
	const refresh = refreshProp || contextRefresh;
	const items = contextSourceData?.list || [];
	useEffect(
		() => {
			if (open && items && items.length > 0) {
				const formattedItems = items.map((item, index) => ({
					id: String(item.sourceID),
					sourceID: Number(item.sourceID),
					sourceName: item.sourceName || `項目 ${index + 1}`,
					sourceNote: item.sourceNote || "",
					enabled: !!item.enabled,
					sort: typeof item.sort === "number" ? item.sort : index,
				}));
				setSortedItems(formattedItems);
			} else if (open) {
				loadItemsFromApi();
			}
		},
		// eslint-disable-next-line
		[open, items]
	);
	const loadItemsFromApi = useCallback(
		async () => {
			const res = await getCustomerResourceSortApi();
			const list = (res && res.result) || [];
			const fresh = list
				.map((x, i) => ({
					id: String(x.sourceID),
					sourceID: Number(x.sourceID),
					sourceName: x.sourceName || `項目 ${i + 1}`,
					sourceNote: x.sourceNote || "",
					enabled: !!x.enabled,
					sort: typeof x.sort === "number" ? x.sort : i,
				}))
				.sort((a, b) => a.sort - b.sort);
			setSortedItems(fresh);
			setSortedItems([]);
		},
		// eslint-disable-next-line
		[getCustomerResourceSortApi]
	);
	// 拖曳啟動條件
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
	// 限制拖曳只能垂直移動
	const restrictToVerticalAxis = ({ transform }) => ({
		...transform,
		x: 0, // 禁止水平移動
	});
	const onDragEnd = ({ active, over }) => {
		if (!over || active.id === over.id) return;
		setSortedItems(prev => {
			if (!prev || prev.length === 0) return prev;
			const oldIndex = prev.findIndex(x => x.id === active.id);
			const newIndex = prev.findIndex(x => x.id === over.id);
			const moved = arrayMove(prev, oldIndex, newIndex);
			return moved.map((x, i) => ({ ...x, sort: i }));
		});
	};
	const handleReset = async () => {
		const originalItems = items.map((item, index) => ({
			id: String(item.sourceID),
			sourceID: Number(item.sourceID),
			sourceName: item.sourceName || `項目 ${index + 1}`,
			sourceNote: item.sourceNote || "",
			enabled: !!item.enabled,
			sort: index,
		}));
		setSortedItems(originalItems);
	};
	const handleSubmit = async () => {
		setSubmitting(true);
		if (!sortedItems || sortedItems.length === 0) {
			return;
		}
		const sortList = sortedItems.map(x => x.sourceID);
		await postCustomerResourceSortApi({ sortList });
		if (typeof refresh === "function") { 
			await refresh();
		}
		if (onClose && typeof onClose === "function") {
			onClose();
		}
		setSubmitting(false);
	};
	return (
		<Dialog open={open} onClose={onClose} TransitionComponent={Slide} TransitionProps={{ direction: "up" }} fullScreen>
			<GradientColorHeader onClose={onClose}>項目排序</GradientColorHeader>
			<DialogContent>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
							<Typography variant="body2">
								<IconButton onClick={handleReset} size="small" disabled={submitting}>
									<Icons.InfoOutlined />
								</IconButton>
								使用右側拖曳把手來調整項目順序
							</Typography>
							<Typography variant="body2">
								<IconButton onClick={handleReset} size="small" disabled={submitting}>
									<Icons.Refresh />
								</IconButton>
								(共 {sortedItems?.length || 0} 個項目)
							</Typography>
						</Stack>
						<TableContainer>
							<DndContext sensors={sensors} onDragEnd={onDragEnd} modifiers={[restrictToVerticalAxis]}>
								<Table>
									<TableHead style={{ backgroundColor: "#1E1E1E" }}>
										<TableRow>
											<TableCell align="center">順序</TableCell>
											<TableCell>項目名稱</TableCell>
											<TableCell>項目說明</TableCell>
											<TableCell></TableCell>
										</TableRow>
									</TableHead>
									<SortableContext items={(sortedItems || []).map(i => i.id)} strategy={verticalListSortingStrategy}>
										<TableBody style={{ backgroundColor: "#303030" }}>
											{(sortedItems || []).map((item, index) => (
												<SortableRow key={item.id} row={item}>
													<TableCell align="center">{index + 1}</TableCell>
													<TableCell>{item.sourceName}</TableCell>
													<TableCell>{item.sourceNote}</TableCell>
													{/* <TableCell style={{ backgroundColor: '#1E1E1E'}}></TableCell> */}
												</SortableRow>
											))}
										</TableBody>
									</SortableContext>
								</Table>
							</DndContext>
						</TableContainer>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleReset} disabled={submitting} color="primary">
					重置順序
				</Button>
				<Button variant="contained" color="primary" onClick={handleSubmit} disabled={submitting}>
					{submitting ? "處理中..." : "確認送出"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
export default CustomerResourceSortDialog;
